import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import {
  AlertTriangle, Bug, Lightbulb, HelpCircle, Send, Clock,
  CheckCircle2, XCircle, Loader, ChevronDown, Trash2, RefreshCw
} from 'lucide-react';

const TIPI = [
  { id: 'bug',          label: 'Bug',           icon: Bug,          color: 'var(--red)',    bg: 'var(--red-dim)'    },
  { id: 'problema',     label: 'Problema',      icon: AlertTriangle,color: 'var(--yellow)', bg: 'var(--yellow-dim)' },
  { id: 'suggerimento', label: 'Suggerimento',  icon: Lightbulb,    color: 'var(--cyan)',   bg: 'var(--cyan-dim)'   },
  { id: 'altro',        label: 'Altro',         icon: HelpCircle,   color: 'var(--purple)', bg: 'var(--purple-dim)' },
];

const PRIORITA = [
  { id: 'bassa', label: 'Bassa', color: 'var(--text-muted)', bg: 'var(--surface)' },
  { id: 'media', label: 'Media', color: 'var(--yellow)',     bg: 'var(--yellow-dim)' },
  { id: 'alta',  label: 'Alta',  color: 'var(--red)',        bg: 'var(--red-dim)'    },
];

const STATO_STYLE = {
  aperta:        { color: 'var(--cyan)',   bg: 'var(--cyan-dim)',   label: 'Aperta',        icon: Clock        },
  in_lavorazione:{ color: 'var(--yellow)', bg: 'var(--yellow-dim)', label: 'In lavorazione',icon: Loader       },
  risolta:       { color: 'var(--green)',  bg: 'var(--green-dim)',  label: 'Risolta',       icon: CheckCircle2 },
  chiusa:        { color: 'var(--text-dim)',bg: 'var(--surface)',   label: 'Chiusa',        icon: XCircle      },
};

function fmtDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('it-IT', { day:'2-digit', month:'short', year:'numeric' })
    + ' ' + d.toLocaleTimeString('it-IT', { hour:'2-digit', minute:'2-digit' });
}

export default function Segnala() {
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin';

  const [segnalazioni, setSegnalazioni] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ titolo: '', descrizione: '', tipo: 'bug', priorita: 'media' });
  const [filterStato, setFilterStato] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/segnalazioni');
      setSegnalazioni(r.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titolo.trim()) return;
    setSending(true);
    try {
      await api.post('/segnalazioni', form);
      setForm({ titolo: '', descrizione: '', tipo: 'bug', priorita: 'media' });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
      load();
    } finally { setSending(false); }
  };

  const cambiaStato = async (id, stato) => {
    await api.patch(`/segnalazioni/${id}/stato`, { stato });
    setSegnalazioni(ss => ss.map(s => s.id === id ? { ...s, stato } : s));
  };

  const elimina = async (id) => {
    if (!window.confirm('Eliminare questa segnalazione?')) return;
    await api.delete(`/segnalazioni/${id}`);
    setSegnalazioni(ss => ss.filter(s => s.id !== id));
  };

  const filtered = segnalazioni.filter(s => !filterStato || s.stato === filterStato);

  return (
    <div className="eden-page eden-page--narrow">
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          {isAdmin ? 'Gestione segnalazioni' : 'Segnala un problema'}
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>
          {isAdmin
            ? 'Tutte le segnalazioni ricevute dagli utenti.'
            : 'Segnala un bug, un problema o un suggerimento. Ti risponderemo il prima possibile.'}
        </p>
      </div>

      {/* Form nuova segnalazione (tutti) */}
      <div className="eden-card" style={{ padding: 24, marginBottom: 28 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 18 }}>
          Nuova segnalazione
        </h2>

        {success && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--green-dim)', border: '1px solid rgba(16,185,129,0.3)',
            borderRadius: 10, padding: '12px 16px', marginBottom: 16,
          }}>
            <CheckCircle2 size={16} color="var(--green)" />
            <span style={{ fontSize: 13, color: 'var(--green)', fontWeight: 500 }}>
              Segnalazione inviata! Gli admin sono stati notificati.
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Tipo selector */}
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>
              Tipo
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {TIPI.map(t => {
                const sel = form.tipo === t.id;
                return (
                  <button key={t.id} type="button"
                    onClick={() => setForm(f => ({ ...f, tipo: t.id }))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                      border: sel ? `1px solid ${t.color}40` : '1px solid var(--border)',
                      background: sel ? t.bg : 'var(--surface)',
                      color: sel ? t.color : 'var(--text-muted)',
                      fontSize: 13, fontWeight: sel ? 600 : 400,
                      transition: 'all 0.15s',
                    }}>
                    <t.icon size={14} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Priorità */}
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>
              Priorità
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {PRIORITA.map(p => {
                const sel = form.priorita === p.id;
                return (
                  <button key={p.id} type="button"
                    onClick={() => setForm(f => ({ ...f, priorita: p.id }))}
                    style={{
                      padding: '5px 14px', borderRadius: 8, cursor: 'pointer',
                      border: sel ? `1px solid ${p.color}40` : '1px solid var(--border)',
                      background: sel ? p.bg : 'var(--surface)',
                      color: sel ? p.color : 'var(--text-muted)',
                      fontSize: 12.5, fontWeight: sel ? 600 : 400,
                      transition: 'all 0.15s',
                    }}>
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Titolo */}
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
              Titolo *
            </label>
            <input
              className="eden-input"
              value={form.titolo}
              onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))}
              placeholder="Descrivi brevemente il problema..."
              required
            />
          </div>

          {/* Descrizione */}
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
              Descrizione dettagliata
            </label>
            <textarea
              className="eden-input"
              value={form.descrizione}
              onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))}
              placeholder="Spiega cosa è successo, come riprodurlo, cosa ti aspettavi..."
              rows={4}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div>
            <button type="submit" disabled={sending || !form.titolo.trim()} className="eden-btn-primary">
              <Send size={14} />
              {sending ? 'Invio in corso...' : 'Invia segnalazione'}
            </button>
          </div>
        </form>
      </div>

      {/* Lista segnalazioni */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            {isAdmin ? 'Tutte le segnalazioni' : 'Le tue segnalazioni'}
            {filtered.length > 0 && (
              <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-dim)', fontWeight: 400 }}>
                ({filtered.length})
              </span>
            )}
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Filtro stato */}
            <div style={{ position: 'relative' }}>
              <select
                value={filterStato}
                onChange={e => setFilterStato(e.target.value)}
                className="eden-select"
                style={{ paddingRight: 28, fontSize: 12.5 }}>
                <option value="">Tutti gli stati</option>
                {Object.entries(STATO_STYLE).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <button onClick={load} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:'6px 8px', cursor:'pointer', color:'var(--text-muted)', display:'flex' }}>
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)', fontSize: 13 }}>
            Caricamento...
          </div>
        ) : filtered.length === 0 ? (
          <div className="eden-card" style={{ padding: '40px', textAlign: 'center' }}>
            <AlertTriangle size={24} color="var(--text-dim)" style={{ marginBottom: 8 }} />
            <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>
              {segnalazioni.length === 0 ? 'Nessuna segnalazione ancora.' : 'Nessuna segnalazione con questo filtro.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map(s => {
              const tipo = TIPI.find(t => t.id === s.tipo) || TIPI[3];
              const prio = PRIORITA.find(p => p.id === s.priorita) || PRIORITA[1];
              const stato = STATO_STYLE[s.stato] || STATO_STYLE.aperta;
              const StatoIcon = stato.icon;
              return (
                <div key={s.id} className="eden-card" style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    {/* Icona tipo */}
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: tipo.bg, border: `1px solid ${tipo.color}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <tipo.icon size={17} color={tipo.color} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Titolo + badge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{s.titolo}</span>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: prio.bg, color: prio.color, fontWeight: 500 }}>
                          {prio.label}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '2px 8px', borderRadius: 999, background: stato.bg, color: stato.color, fontWeight: 500 }}>
                          <StatoIcon size={11} />
                          {stato.label}
                        </div>
                      </div>

                      {/* Descrizione */}
                      {s.descrizione && (
                        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 8 }}>
                          {s.descrizione}
                        </p>
                      )}

                      {/* Meta */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        {isAdmin && s.utente_nome && (
                          <span style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>
                            da <strong style={{ color: 'var(--text-muted)' }}>{s.utente_nome}</strong>
                          </span>
                        )}
                        <span style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{fmtDate(s.creato_il)}</span>

                        {/* Admin: cambio stato */}
                        {isAdmin && (
                          <div style={{ display: 'flex', gap: 5, marginLeft: 'auto' }}>
                            <select
                              value={s.stato}
                              onChange={e => cambiaStato(s.id, e.target.value)}
                              style={{
                                background: 'var(--surface)', border: '1px solid var(--border)',
                                borderRadius: 6, color: 'var(--text-muted)', fontSize: 12,
                                padding: '3px 8px', cursor: 'pointer',
                              }}>
                              {Object.entries(STATO_STYLE).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => elimina(s.id)}
                              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--red)', cursor: 'pointer', padding: '3px 8px', display: 'flex', alignItems: 'center' }}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
